import {useEffect, useMemo, useRef, useState} from "react";
import {Alert, Box, Button, CircularProgress, Stack, Typography} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import {doc, onSnapshot} from "firebase/firestore";
import firebase from "../../core/firebase";

type ProcessingStatus = "processing" | "confirmed" | "failed" | "timeout";

type Props = {
  expectedModuleIds?: string[];
  /** Chamado quando o Stripe/Firestore confirmou; pode ser async — a UI fica em “finalizando” até concluir. */
  onConfirmed?: () => void | Promise<void>;
  onTimeout?: () => void;
  onFailed?: (message: string) => void;
  continueLabel?: string;
  onContinue?: () => void;
};

const TIMEOUT_MS = 5 * 60 * 1000;

const isModuleActive = (moduleStatus: unknown) =>
  moduleStatus === "active" || moduleStatus === "cancel_at_period_end";

export const StripePaymentProcessing = ({
  expectedModuleIds = [],
  onConfirmed,
  onTimeout,
  onFailed,
  continueLabel = "Continuar",
  onContinue,
}: Props) => {
  const [status, setStatus] = useState<ProcessingStatus>("processing");
  const [message, setMessage] = useState("Estamos confirmando seu pagamento com o Stripe.");
  const completionStartedRef = useRef(false);

  const expectedIds = useMemo(
    () => Array.from(new Set(expectedModuleIds.filter(Boolean))),
    [expectedModuleIds]
  );

  useEffect(() => {
    completionStartedRef.current = false;
    const user = firebase.auth.currentUser;
    if (!user) {
      setStatus("failed");
      setMessage("Não foi possível acompanhar o pagamento porque a sessão expirou.");
      return;
    }

    let finished = false;
    const finish = (nextStatus: ProcessingStatus, nextMessage: string) => {
      if (finished) return;
      finished = true;
      setStatus(nextStatus);
      setMessage(nextMessage);
    };

    const timeoutId = window.setTimeout(() => {
      finish(
        "timeout",
        "O pagamento ainda está sendo processado. Você já pode acessar o sistema e usar os módulos gratuitos; os módulos pagos serão liberados automaticamente assim que o Stripe confirmar."
      );
      onTimeout?.();
    }, TIMEOUT_MS);

    const unsubscribe = onSnapshot(
      doc(firebase.db, "customerSubscriptions", user.uid),
      (snap) => {
        if (!snap.exists()) return;
        if (finished) return;

        const data = snap.data() as {
          status?: string;
          modules?: Record<string, {status?: string}>;
        };
        const topLevelStatus = data.status || "";
        const failed = ["past_due", "cancelled", "unpaid", "incomplete_expired"].includes(topLevelStatus);

        if (failed) {
          const failureMessage = "O Stripe não confirmou o pagamento. Verifique o método de pagamento ou tente novamente.";
          finish("failed", failureMessage);
          onFailed?.(failureMessage);
          return;
        }

        const modules = data.modules || {};
        const expectedModulesActive =
          expectedIds.length === 0 ||
          expectedIds.every((id) => isModuleActive(modules[id]?.status));

        if (topLevelStatus === "active" && expectedModulesActive) {
          if (completionStartedRef.current || finished) return;
          completionStartedRef.current = true;
          window.clearTimeout(timeoutId);
          setMessage("Pagamento confirmado! Estamos finalizando no servidor…");
          void (async () => {
            try {
              await onConfirmed?.();
              finish("confirmed", "Pronto! Tudo certo por aqui.");
            } catch (err) {
              const failureMessage =
                err instanceof Error ?
                  err.message :
                  "Pagamento ok, mas falhou ao sincronizar. Atualize a página ou tente novamente.";
              completionStartedRef.current = false;
              finish("failed", failureMessage);
              onFailed?.(failureMessage);
            }
          })();
        }
      },
      () => {
        const failureMessage = "Não foi possível acompanhar a confirmação do pagamento em tempo real.";
        finish("failed", failureMessage);
        onFailed?.(failureMessage);
      }
    );

    return () => {
      window.clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [expectedIds, onConfirmed, onFailed, onTimeout]);

  const isProcessing = status === "processing";
  const icon =
    status === "confirmed" ? <CheckCircleOutlineIcon sx={{fontSize: 54, color: "#14b8a6"}} /> :
    status === "failed" ? <ErrorOutlineIcon sx={{fontSize: 54, color: "#ef4444"}} /> :
    status === "timeout" ? <HourglassTopIcon sx={{fontSize: 54, color: "#f59e0b"}} /> :
    <CircularProgress sx={{color: "#14b8a6"}} />;

  return (
    <Stack spacing={3} alignItems="center" py={3}>
      <Box sx={{height: 64, display: "flex", alignItems: "center", justifyContent: "center"}}>
        {icon}
      </Box>

      <Stack spacing={1} alignItems="center">
        <Typography variant="h6" fontWeight={900} sx={{color: "#f8fafc", textAlign: "center"}}>
          {status === "confirmed" ? "Pagamento confirmado" :
            status === "failed" ? "Pagamento não confirmado" :
            status === "timeout" ? "Pagamento ainda em processamento" :
            "Processando pagamento"}
        </Typography>
        <Typography sx={{color: "rgba(226,232,240,0.72)", textAlign: "center", lineHeight: 1.6}}>
          {message}
        </Typography>
      </Stack>

      {isProcessing ? (
        <Alert severity="success" sx={{width: "100%", bgcolor: "rgba(20,184,166,0.12)", border: "1px solid rgba(20,184,166,0.35)", color: "#e2e8f0"}}>
          <strong>Quase lá.</strong> Esta etapa pode levar alguns segundos — mantenha esta janela aberta até aparecer a confirmação final.
        </Alert>
      ) : null}

      {status === "timeout" || status === "confirmed" ? (
        <Button
          variant="contained"
          fullWidth
          onClick={onContinue}
          sx={{background: "linear-gradient(135deg, #0f766e 0%, #0d9488 40%, #14b8a6 100%)", fontWeight: 800}}
        >
          {continueLabel}
        </Button>
      ) : null}
    </Stack>
  );
};
